import React, { useState, useEffect, useRef, useMemo } from 'react';
import debounce from 'just-debounce-it';
import { useStoreSearchAdminReducedLazyQuery } from '../graphql/queries/storeSearchReduced.generated';
import { StoreSearchStoreReducedFragment } from '../graphql/fragments/storeSearchReduced.generated';
import { Popover, createStyles } from '@mantine/core';
import ClassicLoadingSpinner from './LoadingSpinner/ClassicLoadingSpinner';
import i18n from '@/utils/i18n';

const SEARCH_RESULT_MAX = 5;
const uuidRegex =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const useStyles = createStyles((theme) => ({
  option: {
    cursor: 'pointer',
    padding: '0.5rem',
    fontSize: '0.8rem',
    borderBottom: '1px solid #eee',
    backgroundColor: '#fff',

    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

const StoreSearch = (props: {
  setStoreInfo: (
    uuid: string,
    storeId: string,
    storeName: string,
    brandId?: string | null,
  ) => void;
}) => {
  const { classes } = useStyles();
  const [text, setText] = useState('');
  const [options, setOptions] = useState<StoreSearchStoreReducedFragment[]>([]);
  const [search, { loading }] = useStoreSearchAdminReducedLazyQuery();
  const inputRef = useRef<any>(null);
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [selectedOption, setSelectedOption] =
    useState<StoreSearchStoreReducedFragment | null>(null);
  const setStoreInfoRef = useRef(props.setStoreInfo);

  useEffect(() => {
    setStoreInfoRef.current = props.setStoreInfo;
  }, [props.setStoreInfo]);

  const fetchResults = useMemo(
    () =>
      debounce(async (text: string) => {
        if (!text || text.length < 3) {
          setOptions([]);
          return;
        }

        if (
          selectedOption?.external_vytal_id &&
          selectedOption?.name &&
          text === selectedOption.name
        ) {
          setStoreInfoRef.current(
            selectedOption.external_vytal_id,
            selectedOption.id,
            selectedOption.name,
            selectedOption.brand_id,
          );
          return;
        }

        const { data } = await search({
          fetchPolicy: 'no-cache',
          variables: { query: text, limit: SEARCH_RESULT_MAX },
        });

        if (data) {
          const stores = data.storeSearchAdmin
            .filter((s) => s.store)
            .map((s) => s.store as StoreSearchStoreReducedFragment);
          setOptions(stores);
        }
      }, 500),
    [search, selectedOption],
  );

  useEffect(() => {
    if (text) {
      fetchResults.cancel();
      fetchResults(text);
    }
  }, [text, fetchResults]);

  useEffect(() => {
    if (inputRef.current != null) {
      setDropdownWidth(`${inputRef.current.offsetWidth}px`);
    }
  }, [inputRef]);

  return (
    <div>
      <Popover
        opened={options.length > 0 || loading}
        position="bottom"
        withArrow={false}
      >
        <Popover.Target>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={i18n.t('Common:searchForStore')}
          />
        </Popover.Target>
        <Popover.Dropdown
          style={{
            overflowY: 'auto',
            maxHeight: '4rem',
            border: '1px solid #ccc',
            padding: '0',
            zIndex: 100,
          }}
        >
          {loading && (
            <div
              style={{
                width: dropdownWidth,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ClassicLoadingSpinner />
            </div>
          )}

          {!loading &&
            options.map((opt, index) => (
              <option
                className={classes.option}
                style={{
                  width: dropdownWidth,
                  borderBottom:
                    index === options.length - 1 ? 'none' : '1px solid #eee',
                }}
                onClick={() => {
                  setOptions([]);
                  setSelectedOption(opt);
                  setText(opt.name || '');
                }}
                key={opt.id}
                value={opt.external_vytal_id || ''}
                label={opt.name || 'unknown'}
              />
            ))}
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

export default StoreSearch;
